import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { MessageCircle, Save, RefreshCw } from 'lucide-react';
import { API_URL } from './config';

// Admin UI for xgoBot's day-of-week greeting messages — added 2026-09-10 alongside the backend
// wiring these into GET/POST /control-panel/settings/bot-greetings (routes/control_panel.js).
// Nothing exposed these anywhere before this; changing a message previously meant a code change.
const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const EMPTY_WEEK = DAYS.reduce((acc, d) => ({ ...acc, [d.key]: '' }), {});

const BotGreetingsControl = () => {
  const [greetings, setGreetings] = useState({ passenger: EMPTY_WEEK, driver: EMPTY_WEEK });
  const [tab, setTab] = useState('passenger');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const fetchGreetings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/bot-greetings`);
      const json = await res.json();
      if (json.success && json.data) {
        setGreetings(json.data);
      } else {
        setAlert({ show: true, type: 'error', message: 'Failed to load bot greetings.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to load bot greetings: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGreetings();
  }, []);

  const updateDay = (role, day, value) => {
    setGreetings((prev) => ({ ...prev, [role]: { ...prev[role], [day]: value } }));
  };

  const saveGreetings = async () => {
    setSaving(true);
    setAlert({ show: false, type: '', message: '' });
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/bot-greetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(greetings),
      });
      const json = await res.json();
      if (json.success) {
        setGreetings(json.data);
        setAlert({ show: true, type: 'success', message: 'Bot greetings saved.' });
      } else {
        setAlert({ show: true, type: 'error', message: json.message || 'Failed to save bot greetings.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to save bot greetings: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const activeWeek = greetings[tab] || EMPTY_WEEK;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <MessageCircle size={22} />
        <Typography variant="h5" fontWeight={700}>
          Bot Greetings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The message xgoBot sends a known rider or driver on their first message of each day, before
        the ride/delivery menu buttons. Use <code>{'{username}'}</code> anywhere in the text — it's
        replaced automatically with the person's name when sent.
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="passenger" label="Passenger" />
        <Tab value="driver" label="Driver" />
      </Tabs>

      {DAYS.map((day) => (
        <Card key={day.key} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {day.label}
            </Typography>
            <TextField
              multiline
              minRows={3}
              fullWidth
              size="small"
              value={activeWeek[day.key] ?? ''}
              onChange={(e) => updateDay(tab, day.key, e.target.value)}
            />
          </CardContent>
        </Card>
      ))}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button variant="contained" startIcon={<Save size={16} />} onClick={saveGreetings} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save Greetings'}
        </Button>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={fetchGreetings} disabled={saving || loading}>
          Reload
        </Button>
      </Box>
    </Box>
  );
};

export default BotGreetingsControl;
